// Tipos del API HTTP de TerraKairos — espejo manual de src/api/server.ts.
// TODO: cuando el API exponga /v1/openapi.json, generar estos automáticamente.

/**
 * Breakdown por adapter en `/v1/stats.bySource[]`. `properties` es el
 * número de propiedades únicas que tienen al menos una fila de
 * `property_sources` con ese sourceType (después de dedup); `rows` es el
 * raw count en `property_sources` (puede ser ≥ properties si una
 * propiedad fue match-merge desde dos portales). Los `records*` vienen
 * del último cron run para detectar fuentes que devuelven 0 hace días.
 */
export interface StatsBySource {
  sourceType: string;
  properties: number;
  rows: number;
  lastSuccessAt: string | null;
  lastRunAt: string | null;
  recordsFetched: number | null;
  recordsNew: number | null;
}

/**
 * Cobertura territorial agregada — útil para mostrar la prueba de
 * alcance nacional ("X estados / Y municipios / Z AGEBs"). Solo cuenta
 * propiedades con `cve_ent` ya geocodificado.
 */
export interface StatsCoverage {
  estados: number;
  municipios: number;
  agebs: number;
}

/**
 * Datasets territoriales (no-property): SESNSP, MOFFIN, etc. Refresh
 * mensual o trimestral, no diario; por eso no entran al breakdown por
 * sourceType ni al `/v1/health.adapters`.
 */
export interface StatsTerritorial {
  sourceType: string;
  rows: number;
  municipios: number;
  /** YYYYMM string del periodo más reciente cargado (ej. "202509"). */
  latestPeriod: string | null;
  /**
   * Etiqueta de periodo ya formateada para datasets cuyo periodo no es
   * mensual YYYYMM (ej. SHF trimestral "2026-T1"). Si está presente, el
   * frontend la usa tal cual en vez de `formatPeriod(latestPeriod)`.
   */
  periodText?: string | null;
  lastLoadedAt: string | null;
}

/** Distribución global de propiedades por estado (cve_ent INEGI + conteo). */
export interface StatsByEstado {
  cveEnt: string;
  count: number;
}

/** Bucket del histograma de precios (asking_price) en MXN. `max=null` = abierto. */
export interface StatsPriceBucket {
  label: string;
  min: number;
  max: number | null;
  count: number;
}

export interface StatsResponse {
  counts: Record<string, number>;
  /** Número de fuentes distintas activas (RFC-0021 A1). */
  activeSources?: number;
  bySource?: StatsBySource[];
  coverage?: StatsCoverage;
  byEstado?: StatsByEstado[];
  priceHistogram?: StatsPriceBucket[];
  territorial?: StatsTerritorial[];
  generatedAt: string;
}

export interface AdapterHealth {
  sourceType: string;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lagHours: number | null;
  lastError: string | null;
  stale: boolean;
}

export interface HealthDeepResponse {
  ok: boolean;
  db: boolean;
  thresholdHours: number;
  adapters: AdapterHealth[];
  ts: string;
}

/**
 * Respuesta del endpoint TK-S02-04 (`/v1/health`). A diferencia de
 * `HealthDeepResponse`, cada adapter trae un `status` ya clasificado
 * (`OK`/`STALE`/`ERROR`) listo para colorear la UI sin lógica del lado
 * del cliente.
 */
export type AdapterStatus = 'OK' | 'DEGRADED' | 'STALE' | 'ERROR';

export interface AdapterHealthV1 {
  sourceType: string;
  status: AdapterStatus;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lagHours: number | null;
  lastError: string | null;
  recordsFetched: number | null;
  recordsNew: number | null;
  recordsUpdated: number | null;
  /** Fila de carga manual (RFC-0021 B5): STALE informativo, sin 503. */
  manualLoad?: boolean;
  /** Última corrida conservó solo parte del catálogo (fetch con fallos). */
  lastRunPartial?: boolean;
  /** Mediana de fetched de las últimas 7 corridas exitosas (regla DEGRADED). */
  medianFetched7?: number | null;
}

/**
 * Estado por dataset territorial (mensual). Los thresholds aquí son
 * más laxos (60 días) que para adapters (24-30h) porque el cron es
 * mensual + 1 mes de grace para que el SESNSP publique.
 */
export type TerritorialStatus = 'OK' | 'STALE' | 'NOT_LOADED' | 'ERROR';

export interface TerritorialHealthV1 {
  sourceType: string;
  status: TerritorialStatus;
  lastLoadedAt: string | null;
  coverageMunicipios: number | null;
  latestPeriod: string | null;
  ageDays: number | null;
}

export interface HealthV1Response {
  ok: boolean;
  db: boolean;
  thresholdHours: number;
  summary: { ok: number; stale: number; error: number; degraded?: number; manual?: number };
  adapters: AdapterHealthV1[];
  /** Datasets territoriales (SESNSP, MOFFIN, etc.) — opcional para back compat. */
  territorial?: TerritorialHealthV1[];
  ts: string;
}

/**
 * Resumen de criminalidad por municipio (12 meses más recientes
 * disponibles del SESNSP). Servido por `GET /v1/crime/:cveMun` desde el
 * MV `crime_municipal_summary`.
 *
 * Los nombres de campos son snake_case porque el endpoint hace SELECT *
 * directo del MV — coincide con la convención del resto del API.
 */
export interface CrimeMunicipalSummary {
  cve_mun: string;
  cve_ent: string;
  nom_mun: string | null;
  nom_ent: string | null;
  /** Periodo más antiguo en la ventana current 12m (YYYYMM, ej 202501). */
  yyyymm_min: number;
  /** Periodo más reciente en la ventana current 12m (YYYYMM, ej 202512). */
  yyyymm_max: number;
  /** Inicio ventana prev 12m (12 meses anteriores a current — para YoY). */
  yyyymm_prev_min: number;
  /** Fin ventana prev 12m. */
  yyyymm_prev_max: number;
  /** Inicio ventana 36m (baseline larga). */
  yyyymm_36m_min: number;
  computed_at: string;

  // Current 12 meses
  homicidio_doloso_12m: number;
  robo_casa_12m: number;
  robo_vehiculo_12m: number;
  extorsion_12m: number;
  secuestro_12m: number;
  total_delitos_12m: number;

  // Prev 12 meses (referencia para YoY)
  homicidio_doloso_prev_12m: number;
  robo_casa_prev_12m: number;
  robo_vehiculo_prev_12m: number;
  extorsion_prev_12m: number;
  secuestro_prev_12m: number;
  total_delitos_prev_12m: number;

  // 36 meses (baseline smoothed)
  homicidio_doloso_36m: number;
  total_delitos_36m: number;

  // Delta YoY %. NULL si prev_12m == 0 (no comparable).
  // Convención: > 0 = subió, < 0 = bajó.
  homicidio_doloso_yoy_pct: number | null;
  robo_casa_yoy_pct: number | null;
  robo_vehiculo_yoy_pct: number | null;
  extorsion_yoy_pct: number | null;
  secuestro_yoy_pct: number | null;
  total_delitos_yoy_pct: number | null;
}

export interface CrimeResponse {
  cveMun: string;
  summary: CrimeMunicipalSummary;
  ts: string;
}

export interface PropertyFeature {
  id: string;
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] } | null;
  properties: {
    address: string;
    municipio: string | null;
    cveEnt: string | null;
    channel: string;
    status: string;
    propertyType: string;
    price: number | null;
    avaluoPrice: number | null;
    discountPct: number | null;
    surfaceBuiltM2: number | null;
    surfaceTerrainM2: number | null;
    bedrooms: number | null;
    bathrooms: number | null;
    photoUrl: string | null;
    score: number | null;
    // Analytics expuestos para colorear markers + enriquecer tooltip
    // (espejo de buildGeoJsonQuery en src/api/server.ts).
    opportunityScore: number | null;
    discountVsAvaluoPct: number | null;
    priceM2: number | null;
    crimeYoyPct: number | null;
    cenapredSeismicZone: string | null;
    cenapredFloodRisk: string | null;
    zonaRiesgoFlag: boolean | null;
    hasLitigio: boolean | null;
    isLibreGravamen: boolean | null;
    priceChangePct: number | null;
    priceChangeAt: string | null;
    // RFC-0022 Fase 2: grado CONEVAL de la AGEB, sólo con precisión
    // STREET/COLONIA (NULL por construcción en centroide municipal).
    geocodePrecision: GeocodePrecision | null;
    socioGrado: GradoRezago | null;
  };
}

/** Precisión de la coordenada (migración 0026). */
export type GeocodePrecision = 'STREET' | 'COLONIA' | 'MUNICIPAL_CENTROID' | 'NONE';

export interface PropertyCollection {
  type: 'FeatureCollection';
  features: PropertyFeature[];
  /** Total de coincidencias con los mismos filtros (RFC-0021 A5) — para
   *  avisar truncamiento cuando features se capea en 500. */
  total?: number;
}

/** Detalle completo de una property — espejo de SELECT p.* desde el API. */
export interface PropertyDetail {
  id: string;
  deduplication_key: string;
  address_normalized: string | null;
  address_raw: string | null;
  municipio: string | null;
  cve_ent: string | null;
  cve_mun: string | null;
  cve_ageb: string | null;
  cve_manzana: string | null;
  colonia: string | null;
  codigo_postal: string | null;
  clave_catastral: string | null;
  folio_real: string | null;
  property_type: string;
  legal_channel: string;
  acquisition_status: string;
  condition: string;
  asking_price: number | null;
  avaluo_price: number | null;
  predial_balance: number | null;
  discount_pct: number | null;
  currency: string;
  surface_terrain_m2: number | string | null;
  surface_built_m2: number | string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parking: number | null;
  year_built: number | null;
  photo_urls: string[];
  score_overall: number | null;
  hipoteca_vigente: boolean | null;
  libre_de_gravamen: boolean | null;
  embargo_activo: boolean | null;
  litigio_pendiente: boolean | null;
  ejido_flag: boolean | null;
  zona_riesgo_flag: boolean | null;
  isr_trap_risk: boolean | null;
  auction_start_date: string | null;
  auction_end_date: string | null;
  minimum_bid: number | null;
  first_seen_at: string;
  last_updated_at: string;
  delisted_at: string | null;
  coords_geojson: string | null;
  sources: Array<{
    sourceType: string;
    sourceId: string;
    sourceUrl: string | null;
    fetchedAt: string;
    payloadHash: string;
  }> | null;
  // Breakdown del opportunity score (MV 0014).
  opportunity_score: number | null;
  opp_factor_price: number | null;
  opp_factor_safety: number | null;
  opp_factor_completeness: number | null;
  opp_factor_legal: number | null;
  opp_factor_liquidity: number | null;
  opp_factor_physical: number | null;
  // RFC-0022 Fase 2b: nivel socioeconómico de la AGEB (NULL sin STREET/COLONIA).
  opp_factor_socio: number | null;
  opp_price_m2: number | null;
  cenapred_seismic_zone: string | null;
  cenapred_flood_risk: string | null;
  opp_price_m2_zscore: number | null;
  opp_zone_avg_price_m2: number | null;
  opp_zone_median_price_m2: number | null;
  opp_zone_n_comparables: number | null;
  opp_crime_yoy_pct: number | null;
  opp_homicidio_yoy_pct: number | null;
  // Nuevos en MV 0015: señales legales/financieras infrautilizadas
  opp_discount_vs_avaluo_pct: number | null;
  opp_n_photos: number | null;
  opp_has_litigio: boolean | null;
  opp_is_libre_gravamen: boolean | null;
  opp_computed_at: string | null;
  // Tendencia de mercado SHF (MV 0018 shf_zone_appreciation). Es un índice de
  // apreciación (base 2017=100), no precio absoluto: señal de plusvalía zonal.
  shf_yoy_pct: number | null;
  shf_appreciation_5y_pct: number | null;
  shf_index: number | null;
  shf_anio: number | null;
  shf_trimestre: number | null;
  shf_level: 'municipio' | 'estado' | 'nacional' | null;
  // Nivel socioeconómico (RFC-0022 Fase 2): grado de rezago social CONEVAL
  // de la AGEB + indicadores censales de apoyo. NULL cuando la precisión es
  // MUNICIPAL_CENTROID/NONE — el API no lo asigna, no es que falte el dato.
  geocode_precision: GeocodePrecision | null;
  socio_grado: GradoRezago | null;
  socio_graproes: number | null;
  socio_pct_internet: number | null;
  socio_pct_automovil: number | null;
  socio_pct_piso_tierra: number | null;
  /**
   * Historial completo de precio (RFC-0019), más reciente primero. La
   * primera fila histórica (baseline) trae previousPrice null.
   */
  price_history: Array<{
    askingPrice: number | string;
    previousPrice: number | string | null;
    changedAt: string;
  }> | null;
  /** Funciones que el plan no incluye y se ocultaron en esta respuesta (RFC-0025). */
  locked?: FeatureKey[];
  /** Consumo del cupo de fichas tras abrir ésta (null = no se mide: superadmin). */
  /** `repeat`: ya se había abierto este mes, no descontó (RFC-0028). */
  quota?: { metric: string; used: number; limit: number | null; repeat?: boolean } | null;
}

/**
 * Item de la lista plana en /v1/properties (más ligero que PropertyDetail).
 * NOTA: el API devuelve los keys en snake_case (espejo de SQL); los
 * mantenemos así acá para no añadir una capa de transformación.
 */
export interface PropertyListItem {
  id: string;
  deduplication_key: string;
  address_normalized: string | null;
  municipio: string | null;
  cve_ent: string | null;
  cve_mun: string | null;
  property_type: string;
  legal_channel: string;
  acquisition_status: string;
  asking_price: number | null;
  avaluo_price: number | null;
  discount_pct: number | null;
  currency: string;
  surface_terrain_m2: number | null;
  surface_built_m2: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  score_overall: number | null;
  first_seen_at: string;
  last_updated_at: string;
  photo_url: string | null;
  coords: string | null;
  // Opportunity Score 0-100 calculado por MV 0014. Combina precio vs
  // zona, crimen YoY, completitud del listing, claridad legal y
  // liquidez de tipo de propiedad. NULL si no se ha computado.
  opportunity_score: number | null;
  opp_factor_price: number | null;
  opp_factor_safety: number | null;
  opp_factor_completeness: number | null;
  opp_factor_legal: number | null;
  opp_factor_liquidity: number | null;
  opp_factor_physical: number | null;
  // RFC-0022 Fase 2b: nivel socioeconómico de la AGEB (NULL sin STREET/COLONIA).
  opp_factor_socio: number | null;
  opp_price_m2: number | null;
  cenapred_seismic_zone: string | null;
  cenapred_flood_risk: string | null;
  opp_price_m2_zscore: number | null;
  opp_zone_avg_price_m2: number | null;
  opp_zone_n_comparables: number | null;
  opp_crime_yoy_pct: number | null;
  // Nuevos en MV 0015 (también expuestos en lista para badges en cards)
  opp_discount_vs_avaluo_pct: number | null;
  opp_n_photos: number | null;
  opp_has_litigio: boolean | null;
  opp_is_libre_gravamen: boolean | null;
  zona_riesgo_flag: boolean | null;
  // Apreciación de mercado SHF (interanual) para chip en la card.
  shf_yoy_pct: number | null;
  shf_level: 'municipio' | 'estado' | 'nacional' | null;
  // Último cambio real de precio (price_history, RFC-0019). Negativo =
  // recorte → chip "↓N%" en la card.
  last_price_change_pct: number | null;
  last_price_change_at: string | null;
  // Nivel socioeconómico para el chip (RFC-0022 Fase 2). Ver PropertyDetail.
  geocode_precision: GeocodePrecision | null;
  socio_grado: GradoRezago | null;
}

export interface PropertyListResponse {
  items: PropertyListItem[];
  count: number;
  /** Total absoluto que matchean los filtros (sin LIMIT/OFFSET). */
  total: number;
  limit: number;
  offset: number;
  /** Página 1-indexed para clientes paginados. */
  page: number;
  pages: number;
  nextOffset: number | null;
  /** Funciones enmascaradas por el plan (RFC-0025). */
  locked?: FeatureKey[];
}

/**
 * Sugerencia de lugar devuelta por GET /v1/places. El cliente usa `kind`
 * para decidir el zoom destino (estado=7, municipio=11, colonia=14)
 * y `bbox`/`center` para el flyTo.
 */
export interface PlaceSuggestion {
  kind: 'estado' | 'municipio' | 'colonia';
  id: string;
  name: string;
  cveEnt: string | null;
  cveMun: string | null;
  count: number;
  /** [lngMin, latMin, lngMax, latMax] o null si no hay geometría asociada. */
  bbox: [number, number, number, number] | null;
  /** [lng, lat] o null. */
  center: [number, number] | null;
}

export interface PlacesResponse {
  items: PlaceSuggestion[];
}

export interface FacetItem {
  key: string;
  count: number;
}

export interface PriceRange {
  min: number;
  max: number;
  p25: number;
  p50: number;
  p75: number;
  count_with_price: number;
}

export interface FacetsResponse {
  byEstado: FacetItem[];
  byChannel: FacetItem[];
  byStatus: FacetItem[];
  byPropertyType: FacetItem[];
  priceRange: PriceRange | null;
  total: number;
}

export interface HeatmapItem {
  cve_ageb: string;
  cve_ent: string;
  cve_mun: string;
  inv_pct_deshabitadas: number | null;
  viirs_anomaly_mean: number | null;
  properties_listed: number;
  composite_vacancy_score: string | number | null;
  geometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

export interface HeatmapResponse {
  items: HeatmapItem[];
  count: number;
}

// ─── Capa socioeconómica por AGEB urbana (RFC-0022) ──────────────────────

/** Grados de rezago social CONEVAL, de MEJOR a PEOR condición. */
export const GRADOS_REZAGO = ['Muy bajo', 'Bajo', 'Medio', 'Alto', 'Muy alto'] as const;
export type GradoRezago = (typeof GRADOS_REZAGO)[number];

/**
 * Los porcentajes llegan como string (NUMERIC de Postgres se serializa
 * así para no perder precisión) — hay que Number() antes de compararlos.
 */
export interface SocioAgebItem {
  cvegeo: string;
  cve_ent: string;
  cve_mun: string;
  grado_rezago_social: GradoRezago | null;
  pobtot: number | null;
  tvivparhab: number | null;
  graproes: string | number | null;
  pro_ocup_c: string | number | null;
  pct_internet: string | number | null;
  pct_automovil: string | number | null;
  pct_computadora: string | number | null;
  pct_sin_tic: string | number | null;
  pct_piso_tierra: string | number | null;
  pct_agua_fuera: string | number | null;
  pct_deshabitadas: string | number | null;
  pct_sin_derechohabiencia: string | number | null;
  pct_desocupacion: string | number | null;
  geometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

export interface SocioAgebResponse {
  items: SocioAgebItem[];
  count: number;
  truncated: boolean;
  /** Conteo por grado bajo el mismo filtro, SIN limit — describe el área. */
  byGrado: Partial<Record<GradoRezago, number>>;
  gradosOrdenados: readonly GradoRezago[];
  nota: string;
  fuentes: string[];
}

// ─── Auth + watchlist (RFC-0020) ─────────────────────────────────────────

export type UserRole = 'superadmin' | 'admin' | 'analyst';

/** Organización (cliente) de la cuenta. RFC-0024. */
export interface AuthOrganization {
  id: string;
  name: string;
  plan: string;
  seatLimit: number;
  activeUntil: string | null;
  suspended: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  mustChangePassword: boolean;
  /** null sólo para el superadmin (dueño de la plataforma). */
  organizationId: string | null;
  organization: AuthOrganization | null;
  /** Funciones y cupos efectivos (plan + excepciones). RFC-0025. */
  entitlements: Entitlements;
  principal?: 'user' | 'api_key';
}

// ─── Planes, derechos y consumo (RFC-0025) ───────────────────────────────

export type FeatureKey = 'score_breakdown' | 'price_history' | 'watchlist' | 'export_csv' | 'api_access';
export type LimitKey = 'property_views_per_month' | 'watchlist_max' | 'export_rows_per_month' | 'api_requests_per_month';
export type UsageMetric = 'property_views' | 'export_rows' | 'api_requests';

export interface Entitlements {
  plan: string | null;
  planName: string | null;
  features: Record<FeatureKey, boolean>;
  /** null = ilimitado. */
  limits: Record<LimitKey, number | null>;
}

export interface UsageSummary {
  period: string;
  property_views: number;
  export_rows: number;
  api_requests: number;
}

// ─── Registro y planes públicos (RFC-0027) ──────────────────────────────

/**
 * closed: sin registro público (las cuentas las abre el superadmin; vigente
 * desde el 2026-10-08); approval: las solicitudes esperan aprobación;
 * self_serve: la prueba se activa con el enlace del correo.
 */
export type TrialSignupMode = 'closed' | 'approval' | 'self_serve';

export interface PublicPlan {
  code: string;
  name: string;
  description: string | null;
  priceMonthlyMxn: number | null;
  priceYearlyMxn: number | null;
  defaultSeats: number;
  trialDays: number | null;
  features: Partial<Record<FeatureKey, boolean>>;
  limits: Partial<Record<LimitKey, number | null>>;
}

export interface PlatformSettings {
  trialSignup: { mode: TrialSignupMode };
  mailConfigured: boolean;
}

export interface FeatureDef { label: string; description: string; default: boolean }
export interface LimitDef { label: string; description: string; unit: string; default: number | null; metric?: UsageMetric }

export interface PlanRules {
  features: Partial<Record<FeatureKey, boolean>>;
  limits: Partial<Record<LimitKey, number | null>>;
}

export interface PlanDef {
  code: string;
  name: string;
  description: string | null;
  price_monthly_mxn: number | null;
  price_yearly_mxn: number | null;
  default_seats: number;
  trial_days: number | null;
  features: Partial<Record<FeatureKey, boolean>>;
  limits: Partial<Record<LimitKey, number | null>>;
  is_active: boolean;
  sort_order: number;
  organizations: number;
}

export interface PlansResponse {
  plans: PlanDef[];
  catalog: { features: Record<FeatureKey, FeatureDef>; limits: Record<LimitKey, LimitDef> };
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_by_email?: string | null;
  /** false si quien la creó ya no tiene cuenta activa: revisar si se sigue usando. */
  created_by_active?: boolean | null;
  requests_month?: number;
}

/** GET /v1/admin/organization: lo que un cliente ve de su plan. */
export interface MyOrganization {
  organization: {
    id: string; name: string; plan: string; planName: string | null;
    seatLimit: number; seatsUsed: number; activeUntil: string | null; suspended: boolean;
  };
  entitlements: Entitlements;
  usage: UsageSummary;
  usagePrevious: UsageSummary;
}

/** GET /v1/superadmin/organizations/:id */
export interface OrganizationDetail {
  organization: {
    id: string; name: string; slug: string; plan: string; plan_name: string | null;
    seat_limit: number; active_until: string | null; suspended: boolean; notes: string | null;
    contact_email: string | null; overrides: PlanRules; created_at: string;
  };
  entitlements: Entitlements;
  usage: UsageSummary;
  usagePrevious: UsageSummary;
  usageBySubject: Array<{ subject: string; metric: UsageMetric; count: number; user_email: string | null; key_name: string | null; key_prefix: string | null }>;
  users: AdminUser[];
  apiKeys: ApiKey[];
}

/**
 * Cómo se entregó un acceso (RFC-0026): 'email' = le llegó un enlace para
 * elegir su contraseña; 'response_only' = no hay correo (o falló) y el
 * admin debe entregar la contraseña temporal que viene en la respuesta.
 */
export type AccessDelivery = 'email' | 'response_only';

/** Términos vigentes y si la cuenta ya los aceptó (RFC-0026). null = apagado. */
export interface LegalStatus {
  version: string;
  termsUrl: string;
  privacyUrl: string;
  accepted: boolean;
}

export interface MailStatus {
  configured: boolean;
  from: string;
  replyTo: string | null;
  sandbox: boolean;
}

export interface SuperMetrics {
  period: string;
  email?: MailStatus;
  organizations: { total: number; active: number; trials_active: number; expiring_7d: number; expired: number; suspended: number };
  byPlan: Array<{ plan: string; name: string | null; orgs: number; active: number; mrr_mxn: number | null; active_unpriced: number }>;
  mrrMxn: number;
  activeUnpriced: number;
  users: { active: number; active_7d: number; active_30d: number };
  usage: Record<UsageMetric, number>;
  topByUsage: Array<{ id: string; name: string; plan: string; property_views: number }>;
  pendingAccessRequests: number;
  recentActivity: Array<{ at: string; actor_email: string | null; action: string; organization_name: string | null }>;
}

/** Cliente visto por el superadmin, con uso. */
export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  plan_name?: string | null;
  seat_limit: number;
  active_until: string | null;
  suspended: boolean;
  notes: string | null;
  contact_email?: string | null;
  has_overrides?: boolean;
  created_at: string;
  users_total: number;
  users_active: number;
  last_login_at: string | null;
  property_views_month?: number;
  export_rows_month?: number;
  api_requests_month?: number;
}

export interface AuditEntry {
  id: number;
  at: string;
  actor_email: string | null;
  organization_id: string | null;
  organization_name: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  detail: Record<string, unknown> | null;
}

/** Cupo de asientos de la organización que se administra. */
export interface OrganizationSeats {
  id: string;
  name: string;
  plan: string;
  seatLimit: number;
  seatsUsed: number;
  activeUntil: string | null;
  suspended: boolean;
}

/** Item de la watchlist del usuario: resumen de la propiedad + nota. */
export interface WatchlistItem {
  id: string;
  note: string | null;
  observed_at: string;
  address_normalized: string | null;
  municipio: string | null;
  colonia: string | null;
  cve_ent: string | null;
  property_type: string | null;
  legal_channel: string | null;
  status: string | null;
  asking_price: number | string | null;
  currency: string | null;
  opportunity_score: number | null;
}

/** Cuenta listada en el panel admin. */
export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  organization_id?: string | null;
  organization_name?: string | null;
  active: boolean;
  must_change_password: boolean;
  created_at: string;
  last_login_at: string | null;
}

/** Solicitud de acceso en la bandeja del admin (RFC-0021 §1.4). */
export interface AccessRequest {
  id: string;
  name: string;
  institution: string;
  role_title: string;
  email: string;
  reason: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  created_at: string;
  decided_at: string | null;
  admin_note: string | null;
}
